/**
 * Table pagination with page-size select and numbered or numeric page input.
 */
import React, { useEffect } from "react";
import { Icon, Button, TextField, Box } from "@mui/material";
import { useMemo, useState } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";

import VText from "form/VText";
import VSelect from "form/VSelect";
import { asArray, asFunc, asNumber } from "utils/safeData";

/**
 * Server-side pagination controls for tables and lists.
 *
 * @param {{ currentPage?: number, rowsPerPage?: number, rowCount?: number, paginationRowsPerPageOptions?: Array, onChangePage?: Function, onChangeRowsPerPage?: Function }} props
 */
const BPagination = (props) => {
  const { t } = useTranslation("common");

  const currentPage = asNumber(props.currentPage);
  const rowsPerPage = asNumber(props.rowsPerPage, 10);
  const rowCount = asNumber(props.rowCount);
  const paginationRowsPerPageOptions = asArray(props.paginationRowsPerPageOptions);
  const onChangePage = asFunc(props.onChangePage);
  const onChangeRowsPerPage = asFunc(props.onChangeRowsPerPage);

  const [inputPage, setInputPage] = useState(1);

  useEffect(() => {
    setInputPage(currentPage + 1);
  }, [currentPage]);

  const pageCount = useMemo(() => Math.ceil(rowCount / rowsPerPage), [rowCount, rowsPerPage]);
  const from = rowsPerPage * currentPage + 1;
  const to = rowsPerPage * (currentPage + 1) > rowCount ? rowCount : rowsPerPage * (currentPage + 1);

  /**
   * Notifies the parent of a page index change.
   *
   * @param {number} page Zero-based page index.
   * @returns {void}
   */
  const _onPageChange = (page) => {
    onChangePage(page);
  };

  /**
   * Updates page size and resets to the first page.
   *
   * @param {number} pageSize Rows per page.
   * @returns {void}
   */
  const _onRowsPerPageChange = (pageSize) => {
    onChangeRowsPerPage(pageSize);
    onChangePage(0);
  };

  /**
   * Commits the typed page number when Enter is pressed.
   *
   * @param {React.KeyboardEvent} event Keyboard event.
   * @returns {void}
   */
  const onKeyDown = (event) => {
    if (event.key === "Enter") {
      const actualpage = (inputPage && inputPage > 0 ? Number(inputPage) : 1) - 1;
      _onPageChange(actualpage);
    }
  };

  /**
   * Updates the typed page input, ignoring out-of-range values.
   *
   * @param {React.ChangeEvent<HTMLInputElement>} e Change event.
   * @returns {void}
   */
  const onInputChange = (e) => {
    if (e.target.value > pageCount || (e.target.value !== "" && e.target.value <= 0)) return;
    setInputPage(e.target.value);
  };

  return (
    <Box className="flex items-center justify-center flex-wrap w-full px-2 v-pagination">
      <VText>
        {from} ~ {to} / {rowCount}
      </VText>

      <VText className="ml-auto">{t("Per Page")}:</VText>
      <VSelect className="text-sm p-1.5 text-center w-20" option={rowsPerPage} setOption={(val) => _onRowsPerPageChange(val)} items={paginationRowsPerPageOptions.map((v) => ({ label: v, value: v }))} size="small" />

      <Box className="flex items-center">
        <Button variant="text" color="secondary" className="p-0 min-w-0 w-8 h-8 rounded-full" disabled={currentPage === 0} onClick={() => _onPageChange(0)}>
          <Icon>first_page_outlined</Icon>
        </Button>
        <Button variant="text" color="secondary" className="p-0 min-w-0 w-8 h-8 rounded-full" disabled={currentPage === 0} onClick={() => _onPageChange(currentPage - 1)}>
          <Icon>chevron_left_outlined</Icon>
        </Button>

        {pageCount <= 5 ? (
          [...Array(pageCount).keys()].map((v) => (
            <Button key={v} variant={currentPage === v ? "contained" : "text"} color="secondary" className="p-0 min-w-0 w-8 h-8 rounded-full" disableElevation onClick={() => _onPageChange(v)}>
              {v + 1}
            </Button>
          ))
        ) : (
          <TextField
            type="number"
            size="small"
            value={inputPage}
            onChange={onInputChange}
            onKeyDown={onKeyDown}
            InputLabelProps={{ shrink: true }}
            inputProps={{
              className: "text-sm p-1.5 w-8 text-center v-spin-hide",
              inputMode: "numeric",
            }}
          />
        )}

        <Button variant="text" color="secondary" className="p-0 min-w-0 w-8 h-8 rounded-full" disabled={to >= rowCount} onClick={() => _onPageChange(currentPage + 1)}>
          <Icon>chevron_right_outlined</Icon>
        </Button>
        <Button variant="text" color="secondary" className="p-0 min-w-0 w-8 h-8 rounded-full" disabled={to >= rowCount} onClick={() => _onPageChange(pageCount - 1)}>
          <Icon>last_page_outlined</Icon>
        </Button>
      </Box>
    </Box>
  );
};

BPagination.defaultProps = {
  currentPage: 0,
  rowsPerPage: 10,
  rowCount: 0,
  paginationRowsPerPageOptions: [],
  onChangePage: () => {},
  onChangeRowsPerPage: () => {},
  emitPaginationChange: () => {},
};

BPagination.propTypes = {
  currentPage: PropTypes.number.isRequired,
  rowsPerPage: PropTypes.number.isRequired,
  rowCount: PropTypes.number.isRequired,
  paginationRowsPerPageOptions: PropTypes.array,
  onChangePage: PropTypes.func.isRequired,
  onChangeRowsPerPage: PropTypes.func.isRequired,
  emitPaginationChange: PropTypes.func.isRequired,
};

export default BPagination;
